import {useEffect, useState} from 'react';
import {Control, useForm} from 'react-hook-form';

import {convertTZ, zonedTimeToUtc} from 'shared/lib/datetime';
import {validateServerMetricsRange} from 'features/server/lib/serverMetricsHelper';

export interface IServerMetricsRangeForm {
    fromValue: Date,
    toValue: Date,
}

export interface IUseServerMetricsRangePicker {
    control: Control<IServerMetricsRangeForm>,
    error: string | null,
    disabledDate: (_date?: Date) => boolean,
    handleFromChange: (_value: Date | null) => void,
    handleToChange: (_value: Date | null) => void,
}

export const useServerMetricsRangePicker = (
    from: number,
    to: number,
    retentionDays: number,
    tzString: string | null | undefined,
    onChange: (_from: number, _to: number) => void,
): IUseServerMetricsRangePicker => {
    const [error, setError] = useState<string | null>(null);

    const fromDate = convertTZ(new Date(from * 1000), tzString);
    const toDate = convertTZ(new Date(to * 1000), tzString);
    const maxDate = convertTZ(new Date(), tzString);
    const minDate = convertTZ(new Date((Math.floor(Date.now() / 1000) - retentionDays * 86400) * 1000), tzString);

    const {control, setValue} = useForm<IServerMetricsRangeForm>({
        defaultValues: {fromValue: fromDate, toValue: toDate},
    });

    useEffect(() => {
        setValue('fromValue', fromDate);
        setValue('toValue', toDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, tzString]);

    const commit = (nextFrom: Date, nextTo: Date) => {
        const fromSeconds = Math.floor(zonedTimeToUtc(nextFrom, tzString).getTime() / 1000);
        const toSeconds = Math.floor(zonedTimeToUtc(nextTo, tzString).getTime() / 1000);

        const validationError = validateServerMetricsRange(
            fromSeconds,
            toSeconds,
            retentionDays,
            Math.floor(Date.now() / 1000),
        );

        if (validationError) {
            setError(validationError);
            return;
        }

        setError(null);
        onChange(fromSeconds, toSeconds);
    };

    return {
        control,
        error,
        disabledDate: (date) => !date || date > maxDate || date < minDate,
        handleFromChange: (value) => {
            if (value) {
                commit(value, toDate);
            }
        },
        handleToChange: (value) => {
            if (value) {
                commit(fromDate, value);
            }
        },
    };
};

export default useServerMetricsRangePicker;
